<template>
  <div class="grid-container">
    <div class="grid-item">
      <div class="crossword-grid mx-auto">
        <table>
          <tbody>
            <tr v-for="y in crossword.y" :key="y">
              <td
                v-for="x in crossword.x" :key="x"
                class="crossword-grid-cell"
                :style="cellStyle"
              >
                <input
                  :id="getCellId(x - 1, y - 1)"
                  v-model="grid[x - 1][y - 1]"
                  :class="{
                    'black-cell': isBlackCell(x - 1, y - 1),
                  }"
                  autocomplete="off"
                  disabled
                  readonly
                />
                <span class="clue-number">
                  {{ getCellNumber(x - 1, y - 1) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Crossword } from '@/models/Crossword';
import { Options, Vue } from 'vue-class-component';
import { PropType } from 'vue';
import { undoSeededHash } from '@/utils/crypto';

@Options({
  props: {
      crossword: {
          required: true,
          type: Object as PropType<Crossword>
      },
  }
})
export default class RevealedPuzzle extends Vue {
  
  crossword!: Crossword;
  grid: string[][] = Array.from({ length: 99 }, () => Array(99).fill(''));
  selectedCell = [-1, -1]
  
  // Sizing
  tableSize = 1;

  mounted(): void {
    // Handle resize
    window.addEventListener('resize', this.handleResize);

    // Load Crossword info
    this.derivePuzzleSolution();
    this.handleResize();
    this.$forceUpdate();
  }

  derivePuzzleSolution(): void {
    const { acrossClues, downClues } = this.crossword;
    for (const clue of acrossClues) {
      const undoneHash = undoSeededHash(clue.solution, this.crossword.seed);
      const start = clue.x;
      for (const [index, letter] of [...undoneHash].entries()) {
        this.grid[start + index][clue.y] = letter;
      }
    }
    for (const clue of downClues) {
      const undoneHash = undoSeededHash(clue.solution, this.crossword.seed);
      const start = clue.y;
      for (const [index, letter] of [...undoneHash].entries()) {
        this.grid[clue.x][start + index] = letter;
      }
    }
  }

  public beforeDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
  }
  
  public handleResize(): void {
    const crosswordTable = document.getElementById('crossword-race-box');
    if (crosswordTable) {
      this.tableSize =  crosswordTable.clientWidth / 4;
    }
  }

  isBlackCell(x: number, y: number): boolean {
    return this.crossword.blackCells.some(cell => cell.x === x && cell.y === y);
  }

  getCellNumber(x: number, y: number): number | undefined {
    for (const clue of [...this.crossword.acrossClues, ...this.crossword.downClues]) {
      if (clue.x === x && clue.y === y) {
        return clue.number;
      }
    }
  }
  
  getCellId(x: number, y: number): string {
    return "cell-" + x + "-" + y;
  }

  getClueId(x: number, y: number, xSelect: boolean): string {
    return "clue-" + x + "-" + y + "-" + (xSelect ? "across" : "down");
  }

  get cellStyle(): Record<string, string> {
    const cells = this.crossword.y ? this.crossword.y : 1;
    const cellSize = Math.min(this.tableSize / cells, 50);
    return {
        width: `${cellSize}px`,
        height: `${cellSize}px`,
        maxWidth: `${cellSize}px`,
        maxHeight: `${cellSize}px`,
        minWidth: `${cellSize}px`,
        minHeight: `${cellSize}px`,
        fontSize: `${cellSize * .45}px`,
      };
  }
  
}
</script>
  
<style scoped>
.crossword-grid {
  display: flex;
  justify-content: center; 
  align-items: center;
}
.crossword-grid table {
  border-collapse: collapse;
}
.crossword-grid td {
  border: 1px solid #000;
  position: relative;
  padding: 0;
}
.crossword-grid input {
  width: 100%;
  height: 100%;
  border: none;
  text-transform: uppercase;
  padding-top: calc(.25 * 100%)
}
.crossword-grid input::selection {
  color: inherit !important;
}
.black-cell {
  background-color: #000 !important;
  pointer-events: none;
  border: none;
}
.clue-number {
  position: absolute;
  color: #666;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  top: calc(.05* 100%);
  left: calc(.1* 100%);
  font-size: calc(.5* 100%);
}
.crossword-grid-cell {
    background-color: #fff;
    font-weight: 700;
    color: #000;
}
.crossword-grid-cell input {
    text-align: center; 
    border: none;
    caret-color: transparent;
}
.crossword-grid-cell input::selection {
  background: transparent;
}
.crossword-grid-cell input::-webkit-selection {
  background: transparent;
}
.grid-item {
  padding: 10px;
  text-align: center;
}
</style>
